import { inject, Injectable } from '@angular/core';
import { environment } from '../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { Listing } from '../dto/listing.model';
import { authContext } from './auth.interceptor';

export interface ListingPage {
  listings: Listing[];
  total: number;
}

@Injectable({
  providedIn: 'root',
})
export class ListingsService {
  httpClient = inject(HttpClient);

  private buildFilterParams(filters?: Record<string, unknown>): HttpParams {
    let params = new HttpParams();

    if (!filters) {
      return params;
    }

    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (value != null && value !== '') {
        params = params.set(key, String(value));
      }
    });

    return params;
  }

  createDraft(listing: Listing): Observable<Listing> {
    // force isActive to false for drafts
    listing.isActive = false;
    return this.httpClient.post<Listing>(environment.apiUrl + '/dealers/me/listings', listing, { context: authContext() });
  }

  create(listing: Listing): Observable<Listing> {
    return this.httpClient.post<Listing>(environment.apiUrl + '/dealers/me/listings', listing, { context: authContext() });
  }

  updateDraft(listing: Listing): Observable<Listing> {
    // force isActive to false for drafts
    listing.isActive = false;
    return this.httpClient.put<Listing>(environment.apiUrl + '/dealers/me/listings/' + listing.id, listing, { context: authContext() });
  }

  update(listing: Listing): Observable<Listing> {
    return this.httpClient.put<Listing>(environment.apiUrl + '/dealers/me/listings/' + listing.id, listing, { context: authContext() });
  }

  getMy(): Observable<Listing[]> {
    return this.httpClient.get<Listing[]>(environment.apiUrl + '/dealers/me/listings', { context: authContext() });
  }

  getMyFavoriteListings(): Observable<Listing[]> {
    return this.httpClient.get<Listing[]>(environment.apiUrl + '/users/me/favorite-listings', { context: authContext() });
  }

  getMyDrafts(): Observable<Listing[]> {
    return this.httpClient.get<Listing[]>(environment.apiUrl + '/dealers/me/listings/drafts', { context: authContext() });
  }

  getAll(): Observable<Listing[]> {
    return this.httpClient.get<Listing[]>(environment.apiUrl + '/listings');
  }

  getForDealer(dealerId: number): Observable<Listing[]> {
    return this.httpClient.get<Listing[]>(environment.apiUrl + '/listings', { params: new HttpParams().set('dealerId', dealerId.toString()) });
  }

  getWithSlug(slug: string): Observable<Listing> {
    return this.httpClient.get<Listing>(environment.apiUrl + '/listings/s/' + slug);
  }

  // This method needs authentication, to load your own listings for edit mode
  getWithId(id: string): Observable<Listing> {
    return this.httpClient.get<Listing>(environment.apiUrl + '/listings/' + id, { context: authContext() });
  }

  getWithFilter(page: number, size: number, filters?: any): Observable<Listing[]> {
    let params = this.buildFilterParams(filters);
    params = params.set('page', page.toString());
    params = params.set('pageSize', size.toString());

    return this.httpClient.get<Listing[]>(environment.apiUrl + '/listings', { params });
  }

  getPageWithFilter(page: number, size: number, filters?: Record<string, unknown>): Observable<ListingPage> {
    let params = this.buildFilterParams(filters);
    params = params.set('page', page.toString());
    params = params.set('pageSize', size.toString());

    return this.httpClient.get<Listing[]>(environment.apiUrl + '/listings', {
      params,
      context: authContext(),
      observe: 'response',
    }).pipe(
      map((response) => {
        const listings = response.body ?? [];
        const rawTotal = response.headers.get('X-Total-Count');
        const parsedTotal = rawTotal == null ? Number.NaN : Number.parseInt(rawTotal, 10);

        return {
          listings,
          total: Number.isFinite(parsedTotal) ? parsedTotal : listings.length,
        };
      })
    );
  }

  followListing(listingId: number): Observable<void> {
    return this.httpClient.post<void>(environment.apiUrl + '/listings/' + listingId + '/favorite', null, { context: authContext() });
  }

  unfollowListing(listingId: number): Observable<void> {
    return this.httpClient.delete<void>(environment.apiUrl + '/listings/' + listingId + '/favorite', { context: authContext() });
  }

  archive(listingId: number): Observable<void> {
    return this.httpClient.post<void>(environment.apiUrl + '/dealers/me/listings/' + listingId + '/archive', null, { context: authContext() });
  }

  unarchive(listingId: number): Observable<void> {
    return this.httpClient.post<void>(environment.apiUrl + '/dealers/me/listings/' + listingId + '/unarchive', null, { context: authContext() });
  }
}
